import {
  AlertTriangle,
  Eye,
  EyeOff,
  Grape,
  LockKeyhole,
  ShieldCheck,
  WifiOff,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AUTHENTICATION_REQUIRED_EVENT,
  RemoteAuthenticationError,
} from "../lib/session-auth";
import {
  getSecureOfflineAdapter,
  SecureVaultUnlockError,
} from "../lib/secure-vault";
import {
  isTrustedRestoreSuspended,
  readTrustedDeviceMaterial,
  subscribeTrustedDeviceSignals,
} from "../lib/trusted-device";
import {
  activateSecureReplica,
  discardSecureDeviceReplica,
  discardUnlockedVaultSession,
  FORGET_DEVICE_ERROR_KEY,
  hasPendingVaultConflict,
  invalidateTrustedDeviceSession,
  lockSecureReplica,
  resolveVaultConflictAndOpen,
  restoreTrustedReplica,
  subscribeVaultSync,
  type VaultSyncSnapshot,
  unlockSecureReplica,
  VAULT_SESSION_CLOSING_EVENT,
} from "../lib/vault-sync";
import {
  isVaultSessionLockSupported,
  VaultSessionLockError,
  VaultSessionLockUnavailableError,
  VAULT_SESSION_UNAVAILABLE_MESSAGE,
} from "../lib/vault-session-lock";

const BACKGROUND_LOCK_DELAY_MS = 5 * 60 * 1000;

interface VaultGateProps {
  children: ReactNode;
}

type GateMode = "restoring" | "activate" | "unlock" | "unsupported" | "closing";

const INITIAL_SYNC_SNAPSHOT: VaultSyncSnapshot = {
  status: "idle",
  error: null,
};

function isAutofilled(input: HTMLInputElement): boolean {
  for (const selector of [":autofill", ":-webkit-autofill"]) {
    try {
      if (input.matches(selector)) return true;
    } catch {
      // Certains moteurs ne connaissent qu’un des deux pseudo-sélecteurs.
    }
  }
  return false;
}

function consumeForgetError(): string | null {
  try {
    const message = sessionStorage.getItem(FORGET_DEVICE_ERROR_KEY);
    sessionStorage.removeItem(FORGET_DEVICE_ERROR_KEY);
    return message;
  } catch {
    return null;
  }
}

export function VaultGate({ children }: VaultGateProps) {
  const [adapter] = useState(getSecureOfflineAdapter);
  const [mode, setMode] = useState<GateMode>("restoring");
  const [unlocked, setUnlocked] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [trustedAlready, setTrustedAlready] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(consumeForgetError);
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const [canDiscardUnreadableReplica, setCanDiscardUnreadableReplica] =
    useState(false);
  const [canDiscardIncompleteReplica, setCanDiscardIncompleteReplica] =
    useState(false);
  const [vaultSnapshot, setVaultSnapshot] = useState<VaultSyncSnapshot>(
    INITIAL_SYNC_SNAPSHOT,
  );
  const formRef = useRef<HTMLFormElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const operationGenerationRef = useRef(0);
  const workingRef = useRef(false);

  useEffect(() => subscribeVaultSync(setVaultSnapshot), []);

  useEffect(() => {
    const handleSessionClosing = () => {
      // Le signal n’est émis qu’après acquisition de la lease navigateur.
      // Invalider aussi toute ouverture en vol avant de retirer les enfants.
      operationGenerationRef.current += 1;
      workingRef.current = true;
      setWorking(true);
      setUnlocked(false);
      setMode("closing");
      setPassword("");
      setError(null);
    };
    window.addEventListener(
      VAULT_SESSION_CLOSING_EVENT,
      handleSessionClosing,
    );
    return () => {
      window.removeEventListener(
        VAULT_SESSION_CLOSING_EVENT,
        handleSessionClosing,
      );
    };
  }, []);

  const returnToPassword = useCallback(async (message: string) => {
    await discardUnlockedVaultSession().catch(() => undefined);
    if (!adapter) return;
    // discard émet volontairement le signal closing, qui invalide les travaux
    // antérieurs. Le token de finalisation doit donc être créé après ce signal.
    const generation = ++operationGenerationRef.current;
    const linked = await adapter.hasLinkedReplica().catch(() => false);
    if (generation !== operationGenerationRef.current) return;
    setUnlocked(false);
    setMode(linked ? "unlock" : "activate");
    setPassword("");
    setError(message);
    workingRef.current = false;
    setWorking(false);
  }, [adapter]);

  useEffect(() => {
    let cancelled = false;
    const generation = ++operationGenerationRef.current;

    const restore = async () => {
      if (!adapter || !isVaultSessionLockSupported()) {
        if (!cancelled) {
          setError(
            adapter
              ? VAULT_SESSION_UNAVAILABLE_MESSAGE
              : "Ce navigateur ne permet pas d’ouvrir le coffre local. Utilisez un navigateur récent avec IndexedDB et Web Crypto activés.",
          );
          setMode("unsupported");
        }
        return;
      }

      let linked = false;
      try {
        const hasReplica = await adapter.hasReplica();
        linked = hasReplica ? await adapter.hasLinkedReplica() : false;
        if (!cancelled) setCanDiscardIncompleteReplica(hasReplica && !linked);
      } catch {
        if (!cancelled) {
          setCanDiscardIncompleteReplica(false);
          setCanDiscardUnreadableReplica(true);
          setError(
            "La copie locale est illisible. Elle a été conservée afin de permettre un nouvel essai.",
          );
          setMode("unlock");
        }
        return;
      }
      setCanDiscardUnreadableReplica(false);

      let material: CryptoKey | null = null;
      try {
        material = await readTrustedDeviceMaterial();
      } catch {
        if (!cancelled) {
          setStorageWarning(
            "Le stockage de confiance n’est pas accessible. Le déverrouillage manuel reste disponible.",
          );
        }
      }
      if (cancelled || generation !== operationGenerationRef.current) return;

      setTrustedAlready(material !== null);
      setTrustDevice(material !== null);
      if (!material || isTrustedRestoreSuspended()) {
        setMode(linked ? "unlock" : "activate");
        return;
      }

      setMode("restoring");
      let openedSessionGeneration: number | null = null;
      try {
        openedSessionGeneration = await restoreTrustedReplica(
          material,
          navigator.onLine !== false,
        );
        if (cancelled || generation !== operationGenerationRef.current) {
          await discardUnlockedVaultSession(openedSessionGeneration).catch(
            () => undefined,
          );
          return;
        }
        setUnlocked(true);
      } catch (caught) {
        const invalidTrust =
          (caught instanceof RemoteAuthenticationError && caught.status === 401) ||
          caught instanceof SecureVaultUnlockError;
        if (cancelled || generation !== operationGenerationRef.current) return;
        if (invalidTrust) {
          setTrustedAlready(false);
          setTrustDevice(false);
        }
        if (caught instanceof VaultSessionLockUnavailableError) {
          setError(caught.message);
          setMode("unsupported");
          return;
        }
        setError(
          caught instanceof RemoteAuthenticationError && caught.status === 401
            ? "La session de cet appareil a expiré ou a été révoquée. Saisissez le mot de passe pour la renouveler."
            : caught instanceof SecureVaultUnlockError
              ? "L’ouverture automatique a échoué. La copie chiffrée a été conservée ; saisissez le mot de passe pour réessayer."
              : caught instanceof Error
                ? caught.message
                : "L’ouverture automatique a échoué.",
        );
        setMode(linked ? "unlock" : "activate");
      }
    };

    void restore();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline, { passive: true });
    window.addEventListener("offline", handleOffline, { passive: true });
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleAuthenticationRequired = () => {
      void (async () => {
        await invalidateTrustedDeviceSession().catch(() => undefined);
        setTrustedAlready(false);
        setTrustDevice(false);
        await returnToPassword(
          "Votre session serveur a expiré ou a été révoquée. Saisissez le mot de passe pour continuer.",
        );
      })();
    };
    window.addEventListener(
      AUTHENTICATION_REQUIRED_EVENT,
      handleAuthenticationRequired,
    );
    const unsubscribe = subscribeTrustedDeviceSignals((signal) => {
      void (async () => {
        if (signal === "forgotten") {
          setTrustedAlready(false);
          setTrustDevice(false);
        }
        await returnToPassword(
          signal === "forgotten"
            ? "La confiance a été retirée dans un autre onglet. Le coffre chiffré est intact."
            : "Ma Cave a été verrouillée dans un autre onglet.",
        );
      })();
    });
    return () => {
      unsubscribe();
      window.removeEventListener(
        AUTHENTICATION_REQUIRED_EVENT,
        handleAuthenticationRequired,
      );
    };
  }, [returnToPassword]);

  useEffect(() => {
    if (!unlocked || !adapter || trustedAlready) return;
    let lockTimer: number | undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (hiddenAtRef.current === null) hiddenAtRef.current = Date.now();
        window.clearTimeout(lockTimer);
        lockTimer = window.setTimeout(() => {
          hiddenAtRef.current = null;
          void lockSecureReplica();
        }, BACKGROUND_LOCK_DELAY_MS);
      } else {
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;
        window.clearTimeout(lockTimer);
        if (
          hiddenAt !== null &&
          Date.now() - hiddenAt >= BACKGROUND_LOCK_DELAY_MS
        ) {
          void lockSecureReplica();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility, {
      passive: true,
    });
    return () => {
      window.clearTimeout(lockTimer);
      hiddenAtRef.current = null;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [adapter, trustedAlready, unlocked]);

  const submit = useCallback(async (candidatePassword: string) => {
    if (!adapter || workingRef.current || !candidatePassword) return;
    if (mode === "activate" && !online) {
      setError("Une première connexion Internet est nécessaire sur cet appareil.");
      return;
    }
    const generation = ++operationGenerationRef.current;
    workingRef.current = true;
    setWorking(true);
    setError(null);
    let openedSessionGeneration: number | null = null;
    try {
      const options = { trustDevice, online, trustedAlready };
      const result = mode === "activate"
        ? await activateSecureReplica(candidatePassword, options)
        : await unlockSecureReplica(candidatePassword, options);
      openedSessionGeneration = result.sessionGeneration;
      if (generation !== operationGenerationRef.current) {
        await discardUnlockedVaultSession(openedSessionGeneration).catch(
          () => undefined,
        );
        return;
      }
      setPassword("");
      setCanDiscardUnreadableReplica(false);
      setCanDiscardIncompleteReplica(false);
      setTrustedAlready(result.trusted);
      setTrustDevice(result.trusted);
      setUnlocked(true);
      if (result.warning) {
        setStorageWarning(result.warning);
      }
    } catch (caught) {
      const conflictPending = mode === "activate" && hasPendingVaultConflict();
      if (generation !== operationGenerationRef.current) return;
      setPassword("");
      if (caught instanceof VaultSessionLockUnavailableError) {
        setError(caught.message);
        setMode("unsupported");
        return;
      }
      if (caught instanceof VaultSessionLockError) {
        setError(caught.message);
        return;
      }
      if (mode === "unlock") {
        setError(
          caught instanceof RemoteAuthenticationError && caught.status === 401
            ? "La vérification serveur a échoué. Le coffre chiffré a été conservé."
            : "Mot de passe incorrect ou copie locale endommagée. La copie chiffrée a été conservée.",
        );
      } else {
        setError(
          conflictPending
            ? "Deux copies différentes existent. Saisissez de nouveau le mot de passe puis choisissez celle à conserver."
            : caught instanceof Error
              ? caught.message
              : "L’activation hors ligne n’a pas pu être terminée.",
        );
      }
    } finally {
      workingRef.current = false;
      if (generation === operationGenerationRef.current) {
        setWorking(false);
      }
    }
  }, [adapter, mode, online, trustDevice, trustedAlready, working]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(passwordRef.current?.value ?? password);
  };

  const handleConflictChoice = useCallback(async (
    choice: "local" | "remote",
  ) => {
    if (workingRef.current || !online || !password) return;
    const warning = choice === "remote"
      ? "Utiliser la copie serveur ? La copie locale et ses actions en attente seront remplacées sur cet appareil."
      : "Garder cette copie locale ? La copie serveur actuelle sera remplacée par cette version chiffrée.";
    if (!window.confirm(warning)) return;

    const generation = ++operationGenerationRef.current;
    workingRef.current = true;
    setWorking(true);
    setError(null);
    let completed = false;
    let openedSessionGeneration: number | null = null;
    try {
      const result = await resolveVaultConflictAndOpen(
        choice,
        password,
        { trustDevice, online, trustedAlready },
      );
      if (!result) {
        setError("Le conflit n’a pas pu être résolu. Aucune copie n’a été supprimée.");
        return;
      }
      openedSessionGeneration = result.sessionGeneration;
      if (generation !== operationGenerationRef.current) {
        await discardUnlockedVaultSession(openedSessionGeneration).catch(
          () => undefined,
        );
        return;
      }
      setPassword("");
      setTrustedAlready(result.trusted);
      setTrustDevice(result.trusted);
      setUnlocked(true);
      if (result.warning) {
        setStorageWarning(result.warning);
      }
      completed = true;
    } catch (caught) {
      if (caught instanceof VaultSessionLockUnavailableError) {
        setMode("unsupported");
      }
      setError(
        caught instanceof Error
          ? caught.message
          : "Le conflit n’a pas pu être résolu.",
      );
    } finally {
      if (!completed && openedSessionGeneration !== null) {
        await discardUnlockedVaultSession(openedSessionGeneration).catch(
          () => undefined,
        );
      }
      workingRef.current = false;
      if (generation === operationGenerationRef.current) {
        setWorking(false);
      }
    }
  }, [online, password, trustDevice, trustedAlready]);

  const submitAutofill = useCallback(() => {
    const input = passwordRef.current;
    const form = formRef.current;
    if (!input || !form || working || !input.value || !isAutofilled(input)) return;
    window.requestAnimationFrame(() => form.requestSubmit());
  }, [working]);

  useEffect(() => {
    const handlePageShow = () => submitAutofill();
    window.addEventListener("pageshow", handlePageShow);
    const frame = window.requestAnimationFrame(submitAutofill);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [submitAutofill]);

  if (unlocked) {
    return (
      <>
        {storageWarning ? (
          <div className="trusted-device-notice" role="status">
            {storageWarning}
          </div>
        ) : null}
        {children}
      </>
    );
  }

  const activation = mode === "activate";
  const restoring = mode === "restoring";
  const closing = mode === "closing";
  const activationConflict = activation && vaultSnapshot.status === "conflict";
  const title = activation ? "Activer Ma Cave" : "Déverrouiller Ma Cave";
  const description = activation
    ? "Cette première ouverture vérifie le mot de passe en ligne, chiffre votre cave puis relie cet appareil."
    : "Le mot de passe déchiffre uniquement la copie de cet appareil. Il n’est jamais enregistré par Ma Cave.";
  const newOfflineTrustUnavailable = !online && !trustedAlready;

  return (
    <main className="vault-gate">
      <section className="vault-card" aria-labelledby="vault-title">
        <div className="vault-brand" aria-hidden="true">
          <Grape size={28} />
        </div>
        <p className="eyebrow">Ma Cave · coffre chiffré</p>
        <h1 id="vault-title">
          {restoring
            ? "Appareil de confiance"
            : closing
              ? "Verrouillage de Ma Cave"
            : mode === "unsupported"
              ? "Stockage sécurisé indisponible"
              : title}
        </h1>

        {restoring || closing ? (
          <div className="vault-restoring" role="status" aria-live="polite">
            {closing
              ? <LockKeyhole size={22} aria-hidden="true" />
              : <ShieldCheck size={22} aria-hidden="true" />}
            <div>
              <strong>{closing ? "Verrouillage…" : "Ouverture…"}</strong>
              <p>
                {closing
                  ? "Finalisation sécurisée des changements en cours."
                  : "Validation de la clé locale et de la copie chiffrée."}
              </p>
            </div>
          </div>
        ) : mode === "unsupported" ? (
          <p className="vault-error" role="alert">
            {error ??
              "Ce navigateur ne permet pas d’ouvrir le coffre local. Utilisez un navigateur récent avec IndexedDB, Web Crypto et Web Locks activés."}
          </p>
        ) : (
          <>
            <p className="vault-intro">{description}</p>
            {!online ? (
              <div className="vault-network" role="status">
                <WifiOff size={18} aria-hidden="true" />
                <span>
                  {activation
                    ? "Une première connexion Internet est nécessaire sur cet appareil."
                    : "Hors ligne · déverrouillage local disponible"}
                </span>
              </div>
            ) : null}
            {storageWarning ? (
              <div className="vault-warning" role="status">{storageWarning}</div>
            ) : null}
            {error ? <div className="vault-error" role="alert">{error}</div> : null}
            {activationConflict ? (
              <div className="vault-conflict" role="group" aria-label="Choix de la copie à conserver">
                <p>
                  <AlertTriangle size={18} aria-hidden="true" />
                  Deux copies différentes existent. Aucune n’a été écrasée.
                </p>
                <label htmlFor="vault-conflict-password">Mot de passe</label>
                <input
                  id="vault-conflict-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  required
                  autoFocus
                  maxLength={1024}
                  disabled={working}
                />
                <button
                  className="button button-primary"
                  type="button"
                  disabled={working || !online || !password}
                  onClick={() => void handleConflictChoice("local")}
                >
                  Garder cette copie locale
                </button>
                <button
                  className="button"
                  type="button"
                  disabled={working || !online || !password}
                  onClick={() => void handleConflictChoice("remote")}
                >
                  Utiliser la copie serveur
                </button>
              </div>
            ) : (
              <form ref={formRef} className="vault-form" onSubmit={handleSubmit}>
                <label htmlFor="vault-password">Mot de passe</label>
                <div className="vault-password-field">
                  <LockKeyhole size={18} aria-hidden="true" />
                  <input
                    ref={passwordRef}
                    id="vault-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                    onAnimationStart={(event) => {
                      if (event.animationName === "password-autofilled") submitAutofill();
                    }}
                    required
                    autoFocus
                    maxLength={1024}
                    disabled={working}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <label className="vault-trust-choice">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    disabled={working || newOfflineTrustUnavailable}
                    onChange={(event) => setTrustDevice(event.currentTarget.checked)}
                  />
                  <span>
                    <strong>Faire confiance à cet appareil</strong>
                    <small>
                      Ma Cave pourra s’ouvrir automatiquement sur ce profil navigateur,
                      y compris hors ligne si sa copie locale est disponible.
                    </small>
                  </span>
                </label>
                {newOfflineTrustUnavailable ? (
                  <p className="vault-trust-hint">
                    Reconnectez-vous pour approuver cet appareil. L’ouverture manuelle
                    hors ligne reste possible.
                  </p>
                ) : null}
                <p className="vault-trust-risk">
                  Toute personne pouvant utiliser ce profil navigateur pourra ouvrir la cave.
                  La clé locale est non exportable, mais ce n’est pas une enclave matérielle.
                </p>
                <button
                  className="button button-primary vault-submit"
                  type="submit"
                  disabled={working || !password || (activation && !online)}
                >
                  <ShieldCheck size={18} aria-hidden="true" />
                  {working
                    ? activation ? "Connexion et chiffrement…" : "Ouverture…"
                    : activation ? "Activer cet appareil" : "Ouvrir ma cave"}
                </button>
              </form>
            )}
            {(canDiscardUnreadableReplica ||
              (canDiscardIncompleteReplica && !activationConflict)) ? (
              <button
                className="vault-forget-staging"
                type="button"
                onClick={() => {
                  if (window.confirm(
                    canDiscardUnreadableReplica
                      ? "Effacer cette copie locale illisible ? Son contenu et ses actions en attente seront perdus sur cet appareil, mais le coffre distant restera intact."
                      : "Effacer la copie locale inachevée ? Les actions qu’elle contient seront perdues, mais le coffre distant restera intact.",
                  )) {
                    void discardSecureDeviceReplica(
                      canDiscardUnreadableReplica ? "unreadable" : "staging",
                    );
                  }
                }}
              >
                {canDiscardUnreadableReplica
                  ? "Supprimer la copie locale illisible"
                  : "Supprimer la copie locale inachevée"}
              </button>
            ) : null}
            <details className="vault-limits">
              <summary>À savoir sur l’accès de confiance</summary>
              <p>
                La confiance est liée à ce navigateur et peut être retirée avec
                « Oublier cet appareil ». Une révocation distante ne peut pas bloquer
                instantanément une copie déjà hors ligne. Le coffre reste chiffré,
                mais une faille exécutée sur cette origine pourrait utiliser la clé
                locale pendant l’ouverture.
              </p>
            </details>
          </>
        )}
      </section>
    </main>
  );
}
